import {
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Typography,
} from "@mui/material";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  toggleAlert,
  toggleBilledFilter,
  toggleReportType,
  toggleTransmissionType,
  toggleMyPatients,
  togglePriorityType,
  toggleSavedReports,
  toggleAuditedReports,
} from "app/store/transmission/slice/transmissionSlice";
import { MRT_GlobalFilterTextField as GlobalFilterTextField } from "material-react-table";
import ColumnToggle from "components/common/table/ColumnToggle";
import {
  EVOKE_ACTIVE,
  EVOKE_PROCESSED,
  EVOKE_ROLES,
  HF_BILLING_CODES,
} from "static/constants";
import ToggleButtons from "components/common/toggle_button/ToggleButton";
import { isValidArray } from "utils/array";
import { useLocation } from "react-router";
import { isPatchMonitoringRoute } from "pages/patchMonitoring/patchMonitoring.config";
import { resolveMyPatientsFilter } from "utils/transmissionTable.query";

const TransmissionToolbar = ({ table }) => {
  const { pathname } = useLocation();

  /* Connectivity status, report type and transmission type have no meaning for a patch
   * report, and two of them would empty the grid, which reads as a fault. */
  const isPatchWorklist = isPatchMonitoringRoute(pathname);

  const {
    isFilterAlert,
    isHideBilledFilter,
    isMyPatientsFilter,
    selectedTab,
    reportType,
    transmissionType,
    priorityType,
    showSavedReports,
    showAuditedReports,
    reportsMeta: { alertCount, connectivityStatusCount } = {},
  } = useSelector((state) => state.transmission);

  const { currentUser } = useSelector((state) => state.auth);
  const { user_prefs, is_auditor } = currentUser || {};
  const { accountsMapper } = useSelector((state) => state.account);

  const dispatch = useDispatch();

  const role = localStorage.getItem("role");
  const clinicName = localStorage.getItem("clinicName");
  const physician = localStorage.getItem("physician") === "true";

  // Until the clinician picks, the box shows what their saved preference resolves to —
  // the same value the report list is being filtered by.
  const myPatients =
    isMyPatientsFilter ??
    resolveMyPatientsFilter({
      view: selectedTab,
      showOnlyAssignedReports:
        accountsMapper?.[clinicName]?.settings?.show_only_assigned_reports,
      myPatientsPreference: user_prefs?.my_patients_toggled,
      storedMyPatientsToggled: localStorage.getItem(
        `myPatientsToggled:${currentUser?.userId}`
      ),
    });

  const renderDivider = (
    <Divider orientation="vertical" variant="middle" className="divider" />
  );

  const reportTypeButtons = useMemo(() => {
    const baseButtons = [
      { value: "all", label: "All" },
      { value: "regular", label: "Regular" },
      { value: "hf", label: "HF" },
    ].map((btn) => ({
      ...btn,
      color: "success",
      style: { textTransform: "none" },
    }));

    const allowedCodes = user_prefs?.allowed_billing_codes;

    // Return all if no restrictions exist
    if (!isValidArray(allowedCodes)) return baseButtons;

    // Determine availability in one pass
    const hasHF = allowedCodes.some((code) => HF_BILLING_CODES.includes(code));
    const hasRegular = allowedCodes.some(
      (code) => !HF_BILLING_CODES.includes(code)
    );

    // Filter based on flags
    const filtered = baseButtons.filter((btn) => {
      if (btn.value === "regular") return hasRegular;
      if (btn.value === "hf") return hasHF;
      return false;
    });

    // Prepend "All" only if multiple options exist
    return filtered.length > 1 ? [baseButtons[0], ...filtered] : filtered;
  }, [user_prefs?.allowed_billing_codes]);

  return (
    <div className="table-toolbar">
      <div className="d-flex justify-between">
        <div className="d-flex align-items-center">
          <GlobalFilterTextField table={table} />
          <ColumnToggle
            table={table}
            columnGroups={[{ label: "EHR", columns: ["oru", "dft"] }]}
          />
        </div>
        <div className="d-flex align-items-center">
          {user_prefs?.note_suggestions_enabled &&
            selectedTab === EVOKE_ACTIVE && (
              <>
                <>
                  <Typography variant="body1" style={{ marginRight: "8px" }}>
                    Priority
                  </Typography>
                  <FormControlLabel
                    style={{ margin: 0 }}
                    control={
                      <ToggleButtons
                        size="small"
                        value={priorityType}
                        style={{
                          maxHeight: "35px",
                        }}
                        sx={{
                          "& .Mui-selected": {
                            backgroundColor: "#F1F6E3",
                            color: "#638215",
                          },
                        }}
                        onChange={(e, c) => {
                          dispatch(togglePriorityType(e.target.value));
                        }}
                        buttons={[
                          {
                            value: "all",
                            label: "All",
                            color: "success",
                            style: { textTransform: "none" },
                          },
                          {
                            value: "less",
                            label: "Less",
                            color: "success",
                            style: { textTransform: "none" },
                          },
                          {
                            value: "high",
                            label: "High",
                            color: "success",
                            style: { textTransform: "none" },
                          },
                        ]}
                      />
                    }
                  />
                </>
                {renderDivider}
              </>
            )}

          {role === "clinic" && !isPatchWorklist && (
            <FormControlLabel
              className="mr-3"
              control={
                <Checkbox
                  onChange={(e, c) => {
                    dispatch(
                      toggleTransmissionType(c ? "connectivityStatus" : "all")
                    );
                  }}
                  checked={transmissionType === "connectivityStatus"}
                  name="connectivityStatus "
                />
              }
              label={
                <>
                  Connectivity Status Report{" "}
                  {connectivityStatusCount > 0 && (
                    <Chip label={connectivityStatusCount} color="secondary" />
                  )}
                </>
              }
            />
          )}

          {reportTypeButtons?.length > 1 && !isPatchWorklist && (
            <FormControlLabel
              style={{ margin: 0 }}
              control={
                <ToggleButtons
                  size="small"
                  value={reportType}
                  style={{
                    maxHeight: "35px",
                  }}
                  sx={{
                    "& .Mui-selected": {
                      backgroundColor: "#F1F6E3",
                      color: "#638215",
                    },
                  }}
                  onChange={(e, c) => {
                    dispatch(toggleReportType(e.target.value));
                  }}
                  buttons={reportTypeButtons}
                />
              }
            />
          )}

          {/* Divider belongs to the transmission-type toggle below — hiding the toggle
              without it would leave a stray vertical rule at the end of the row. */}
          {!isPatchWorklist && renderDivider}

          {!isPatchWorklist && (
            <FormControlLabel
              style={{ margin: 0 }}
              control={
                <ToggleButtons
                  size="small"
                  value={transmissionType}
                  style={{ maxHeight: "35px" }}
                  sx={{
                    "& .Mui-selected": {
                      backgroundColor: "#F1F6E3",
                      color: "#638215",
                    },
                  }}
                  onChange={(e, c) => {
                    dispatch(toggleTransmissionType(e.target.value));
                  }}
                  buttons={[
                    {
                      value: "all",
                      label: "All",
                      color: "success",
                      style: { textTransform: "none" },
                    },
                    {
                      value: "remote",
                      label: "Remote",
                      color: "success",
                      style: { textTransform: "none" },
                    },
                    {
                      value: "inOffice",
                      label: "In Office",
                      color: "success",
                      style: { textTransform: "none" },
                    },
                  ]}
                />
              }
            />
          )}
        </div>
      </div>
      <div className="d-flex justify-end mt-2">
        {(user_prefs?.audited_reports || is_auditor) &&
          selectedTab === EVOKE_PROCESSED && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={(_e, c) => dispatch(toggleAuditedReports(c))}
                    checked={showAuditedReports}
                    name="auditedReports"
                  />
                }
                label="Audited Reports"
              />
            </>
          )}
        {EVOKE_ROLES.includes(role) && selectedTab === EVOKE_ACTIVE && (
          <>
            <FormControlLabel
              style={{ margin: 0 }}
              control={
                <Checkbox
                  onChange={(_e, c) => dispatch(toggleSavedReports(c))}
                  checked={showSavedReports}
                  name={"savedReports"}
                />
              }
              label="Saved Reports"
            />
            {renderDivider}
          </>
        )}

        {physician &&
          selectedTab === "clinic-review" &&
          !accountsMapper[clinicName]?.settings?.show_only_assigned_reports && (
            <>
              <FormControlLabel
                style={{ margin: 0 }}
                control={
                  <Checkbox
                    onChange={(e, c) => dispatch(toggleMyPatients(c))}
                    checked={myPatients}
                    name={"myPatients"}
                    style={{ paddingLeft: "0" }}
                  />
                }
                label="My Patients"
              />
              {renderDivider}
            </>
          )}

        {renderDivider}

        <FormControlLabel
          style={{ margin: 0 }}
          control={
            <Checkbox
              onChange={(e, c) => dispatch(toggleAlert(c))}
              checked={isFilterAlert}
              name={"FA"}
              value={"FA"}
              style={{ paddingLeft: "0" }}
            />
          }
          label={
            <>Transmission Alerts {alertCount > 0 ? ` (${alertCount})` : ""}</>
          }
        />

        {renderDivider}

        {role === "clinic" && (
          <FormControlLabel
            style={{ margin: 0 }}
            control={
              <Checkbox
                onChange={(e, c) => dispatch(toggleBilledFilter(c))}
                checked={isHideBilledFilter}
                name={"FB"}
                value={"FB"}
                style={{ paddingLeft: "0" }}
              />
            }
            label="Hide Billed"
          />
        )}
      </div>
    </div>
  );
};

export default TransmissionToolbar;
